/**
 * Nespresso Recycling Program - Tier Configuration
 *
 * Based on the spec in docs/ui.md:
 * Coffee-forward tier naming with specific colors and thresholds.
 */

/**
 * Represents a reward that can be claimed at a milestone tier.
 */
export interface TierReward {
  id: string;
  name: string;
  description: string;
  claimInstructions: string;
  image?: string;
  value?: string;
}

/**
 * Represents a milestone tier in the loyalty program.
 */
export interface Tier {
  id: string;
  name: string;
  threshold: number;
  descriptor: string;
  badge: {
    icon: string;
    color: string;
    colorName: string;
  };
  reward: TierReward;
}

/**
 * All milestone tiers in the Nespresso loyalty program.
 * Tiers are ordered by threshold (ascending).
 *
 * From spec:
 * - Lungo: Entry/First return - "Your journey begins"
 * - Espresso: 100 capsules - "Finding your rhythm"
 * - Ristretto: 300 capsules - "Concentrated commitment"
 * - Intenso: 750 capsules - "Depth of dedication"
 * - Grand Cru: 1,500+ capsules - "The pinnacle of purpose"
 */
export const TIERS: Tier[] = [
  {
    id: 'lungo',
    name: 'Lungo',
    threshold: 0, // Entry / First return
    descriptor: 'Your journey begins',
    badge: {
      icon: 'coffee',
      color: '#D4C4A8', // Warm Cream
      colorName: 'Warm Cream',
    },
    reward: {
      id: 'reward-lungo',
      name: 'Welcome to the Program',
      description: 'Thank you for joining the Nespresso Recycling Program',
      claimInstructions: 'Your recycling journey has begun. Keep recycling to unlock rewards!',
      value: 'Welcome',
    },
  },
  {
    id: 'espresso',
    name: 'Espresso',
    threshold: 100,
    descriptor: 'Finding your rhythm',
    badge: {
      icon: 'coffee-bean',
      color: '#8B5A2B', // Roasted Copper
      colorName: 'Roasted Copper',
    },
    reward: {
      id: 'reward-espresso',
      name: 'Espresso Reward',
      description: 'A complimentary sleeve of our bestselling Original Line pods',
      claimInstructions: 'Show this screen to a Nespresso boutique associate or use code ESPRESSO100 online',
      value: '$8 value',
    },
  },
  {
    id: 'ristretto',
    name: 'Ristretto',
    threshold: 300,
    descriptor: 'Concentrated commitment',
    badge: {
      icon: 'award',
      color: '#BD6416', // Rich Amber
      colorName: 'Rich Amber',
    },
    reward: {
      id: 'reward-ristretto',
      name: 'Ristretto Reward',
      description: 'Choose a premium Nespresso travel mug in your favorite color',
      claimInstructions: 'Visit any Nespresso boutique or redeem online with code RISTRETTO300',
      value: '$25 value',
    },
  },
  {
    id: 'intenso',
    name: 'Intenso',
    threshold: 750,
    descriptor: 'Depth of dedication',
    badge: {
      icon: 'medal',
      color: '#4A3728', // Deep Bronze
      colorName: 'Deep Bronze',
    },
    reward: {
      id: 'reward-intenso',
      name: 'Intenso Reward',
      description: 'An exclusive limited edition sleeve featuring rare and seasonal blends',
      claimInstructions: 'Limited edition sleeves ship directly to you. Confirm your address in your Nespresso account',
      value: '$35 value',
    },
  },
  {
    id: 'grand-cru',
    name: 'Grand Cru',
    threshold: 1500,
    descriptor: 'The pinnacle of purpose',
    badge: {
      icon: 'crown',
      color: '#1A1A1A', // Obsidian & Gold
      colorName: 'Obsidian & Gold',
    },
    reward: {
      id: 'reward-grand-cru',
      name: 'Grand Cru Experience',
      description: 'An exclusive VIP boutique visit with personal coffee consultation and tasting',
      claimInstructions: 'Schedule your VIP experience by calling your local boutique or through the Nespresso app',
      value: '$100 value',
    },
  },
];

/**
 * Get a tier by its ID.
 */
export function getTierById(id: string): Tier | undefined {
  return TIERS.find((tier) => tier.id === id);
}

/**
 * Get the current tier for a given pod count.
 * Returns the highest tier the user has achieved.
 */
export function getCurrentTier(podCount: number): Tier | undefined {
  // Sort tiers by threshold descending and find the first one the user qualifies for
  const sortedTiers = [...TIERS].sort((a, b) => b.threshold - a.threshold);
  return sortedTiers.find((tier) => podCount >= tier.threshold);
}

/**
 * Alias for getCurrentTier - gets tier by pod count.
 * Always returns a tier (defaults to first tier if none matched).
 */
export const getTierByPodCount = (podCount: number): Tier => {
  const tier = getCurrentTier(podCount);
  return tier || TIERS[0];
};

/**
 * Get the next tier the user can achieve.
 * Returns undefined if the user has reached the highest tier.
 * Accepts either a pod count (number) or a current tier (Tier).
 */
export function getNextTier(podCountOrTier: number | Tier): Tier | undefined {
  const threshold = typeof podCountOrTier === 'number'
    ? podCountOrTier
    : podCountOrTier.threshold;

  // Sort tiers by threshold ascending and find the first one above current threshold
  const sortedTiers = [...TIERS].sort((a, b) => a.threshold - b.threshold);
  return sortedTiers.find((tier) => tier.threshold > threshold);
}

/**
 * Calculate progress toward the next tier.
 */
export function getTierProgress(podCount: number): {
  currentCount: number;
  nextThreshold: number | null;
  progressPercent: number;
  currentTier: Tier | undefined;
  nextTier: Tier | undefined;
} {
  const currentTier = getCurrentTier(podCount);
  const nextTier = getNextTier(podCount);

  if (!nextTier) {
    // User has reached max tier
    return {
      currentCount: podCount,
      nextThreshold: null,
      progressPercent: 100,
      currentTier,
      nextTier: undefined,
    };
  }

  const previousThreshold = currentTier?.threshold ?? 0;
  const range = nextTier.threshold - previousThreshold;
  const progress = podCount - previousThreshold;
  const progressPercent = Math.min(100, Math.max(0, (progress / range) * 100));

  return {
    currentCount: podCount,
    nextThreshold: nextTier.threshold,
    progressPercent,
    currentTier,
    nextTier,
  };
}

/**
 * Get progress to next tier with pods remaining calculation.
 */
export function getProgressToNextTier(podCount: number): {
  progressPercent: number;
  podsRemaining: number;
} {
  const progress = getTierProgress(podCount);
  const podsRemaining = progress.nextThreshold
    ? progress.nextThreshold - podCount
    : 0;

  return {
    progressPercent: progress.progressPercent,
    podsRemaining,
  };
}

/**
 * Check if a user has just reached a new milestone.
 * Useful for triggering celebration animations.
 */
export function checkMilestoneReached(
  previousCount: number,
  newCount: number
): Tier | undefined {
  return TIERS.find(
    (tier) => previousCount < tier.threshold && newCount >= tier.threshold
  );
}

/**
 * Get all rewards for tiers the user has unlocked.
 */
export function getUnlockedRewards(podCount: number): TierReward[] {
  return TIERS
    .filter((tier) => podCount >= tier.threshold)
    .map((tier) => tier.reward);
}

/**
 * Get reward data for a specific tier threshold.
 */
export function getRewardForMilestone(threshold: number): TierReward | undefined {
  const tier = TIERS.find((t) => t.threshold === threshold);
  return tier?.reward;
}
